import { FC, useEffect, useState } from 'react';
import ReactMarkdown, { defaultUrlTransform, type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Check, Copy } from 'lucide-react';
import { sanitizeAiText } from '@/utils/sanitizeAiText';

// 复制按钮：代码块与消息级复制共用同一套样式/交互（点击后 1.5s 变勾）。
// 剪贴板不可用（非安全上下文等）时不给假反馈
export const CopyButton: FC<{ text: string; title?: string; className?: string }> = ({
  text,
  title = '复制',
  className = '',
}) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // 复制失败静默：不置 copied，避免「已复制」假反馈
    }
  };
  return (
    <button
      onClick={handleCopy}
      title={copied ? '已复制' : title}
      className={`flex items-center gap-1 px-1.5 py-0.5 rounded-[2px] text-[10px] text-text-muted hover:text-text-primary hover:bg-white/[0.06] transition-colors ${className}`}
    >
      {copied ? <Check size={11} className="text-success" /> : <Copy size={11} />}
      {copied && <span>已复制</span>}
    </button>
  );
};

// 代码块卡片：深色容器 + 右上角复制按钮，代码区横向滚动、等宽、小一号。
// v1 不做语法高亮（不引 highlight/prism 系依赖）
const CodeBlock: FC<{ language?: string; code: string }> = ({ language, code }) => (
  <div className="my-2 rounded-[2px] border border-white/[0.08] bg-black/30">
    <div className="flex items-center justify-between px-2.5 py-1 border-b border-white/[0.08]">
      <span className="text-[10px] font-mono text-text-muted">{language || '代码'}</span>
      <CopyButton text={code} title="复制代码" />
    </div>
    <pre className="overflow-x-auto px-3 py-2 text-xs leading-relaxed">
      <code className="font-mono text-text-primary">{code}</code>
    </pre>
  </div>
);

// 链接安全口径（保守）：AI 输出里的链接只放行 http/https，javascript:/data: 等
// 一律剥成纯文本；站内引用只用 [n] 纯编号，真链接极少，安全优先
const SAFE_HREF = /^https?:\/\//i;

// URL 白名单：react-markdown 默认 urlTransform 只放行 http(s)/irc(s)/mailto/xmpp，
// 自定义伪协议会被清空成空 href（百科 [来源 N] 的 wikisrc: 引用芯片 0.2.120 实捕
// 回归——href 变 '' 后自定义 a 渲染器匹配不上，芯片降级成纯文本）。
// wikisrc:/wikientry: 永不直达 DOM（渲染器拦截成站内 Link），显式放行，其余仍走默认口径
const WIKI_PSEUDO_PROTO = /^(wikisrc|wikientry):/;
const urlTransform = (url: string) => (WIKI_PSEUDO_PROTO.test(url) ? url : defaultUrlTransform(url));

// 自定义渲染：深色主题排版，类名与项目 glass/text-text-*/border-white/[0.08] 体系一致。
// 注意每个渲染器都要把 node 解构出来（按 ESLint 约定命名 _node）——它是 remark AST
// 节点，漏了会漏到 DOM 上触发 React 警告
const COMPONENTS: Components = {
  // 表格是本次核心诉求：外层横向滚动兜底，表头浅底，单元格细边框紧凑 padding
  table: ({ node: _node, ...props }) => (
    <div className="my-2 overflow-x-auto rounded-[2px] border border-white/[0.08]">
      <table className="w-full border-collapse text-xs" {...props} />
    </div>
  ),
  th: ({ node: _node, ...props }) => (
    <th className="px-3 py-1.5 text-left font-medium whitespace-nowrap text-text-primary bg-white/[0.04] border-b border-white/[0.08]" {...props} />
  ),
  td: ({ node: _node, ...props }) => (
    <td className="px-3 py-1.5 align-top text-text-secondary border-b border-white/[0.08]" {...props} />
  ),
  // 行内 code 与块级 code 的分流：v9+ 没有 inline prop，用语言标记或换行符判定
  // （行内 code 不会跨行）；块级交给 CodeBlock 卡片
  code: ({ node: _node, className, children, ...props }) => {
    const text = String(children);
    const language = /language-(\w+)/.exec(className || '')?.[1];
    if (language || text.includes('\n')) {
      return <CodeBlock language={language} code={text.replace(/\n$/, '')} />;
    }
    return (
      <code className="px-1 py-0.5 rounded-[2px] bg-white/[0.06] font-mono text-[13px] text-info" {...props}>
        {children}
      </code>
    );
  },
  // 块级 code 的 <pre> 外壳直接拆掉（样式全在 CodeBlock 里），避免 pre 默认样式干扰
  pre: ({ node: _node, children }) => <>{children}</>,
  a: ({ node: _node, href, children, ...props }) => {
    if (!href || !SAFE_HREF.test(href)) {
      return <span className="text-text-secondary">{children}</span>;
    }
    return (
      <a href={href} target="_blank" rel="noreferrer" className="text-info underline hover:opacity-80" {...props}>
        {children}
      </a>
    );
  },
  // AI 输出里的图床链接不可靠，不渲染远程图，降级为链接文字
  img: ({ node: _node, src, alt }) => {
    const url = typeof src === 'string' ? src : '';
    const label = alt || url || '图片';
    return SAFE_HREF.test(url) ? (
      <a href={url} target="_blank" rel="noreferrer" className="text-info underline hover:opacity-80">{label}</a>
    ) : (
      <span className="text-text-muted">{label}</span>
    );
  },
  h1: ({ node: _node, ...props }) => <h1 className="mt-3 mb-1.5 text-base font-semibold text-text-primary" {...props} />,
  h2: ({ node: _node, ...props }) => <h2 className="mt-3 mb-1 text-[15px] font-semibold text-text-primary" {...props} />,
  h3: ({ node: _node, ...props }) => <h3 className="mt-2.5 mb-1 text-sm font-semibold text-text-primary" {...props} />,
  p: ({ node: _node, ...props }) => <p className="my-1.5 first:mt-0 last:mb-0" {...props} />,
  ul: ({ node: _node, ...props }) => <ul className="my-1.5 list-disc pl-5 space-y-0.5" {...props} />,
  ol: ({ node: _node, ...props }) => <ol className="my-1.5 list-decimal pl-5 space-y-0.5" {...props} />,
  blockquote: ({ node: _node, ...props }) => (
    <blockquote className="my-2 border-l-2 border-white/[0.08] pl-3 text-text-muted" {...props} />
  ),
  hr: ({ node: _node, ...props }) => <hr className="my-3 border-border-color" {...props} />,
  strong: ({ node: _node, ...props }) => <strong className="font-semibold text-text-primary" {...props} />,
};

interface MarkdownTextProps {
  content: string;
  className?: string;
  /** 自定义渲染器覆盖（浅合并 over 默认渲染器）：页面有特殊节点时用（如百科 [来源 N] 引用芯片） */
  components?: Components;
}

// 长文分段渲染（0.2.168）：整篇一次性 react-markdown 解析会把主线程钉住数秒——
// 真机库实测 74 篇 ≥3 万字、最长 238,247 字（性能报告 2026-10-19 第 4 项）。
// 做法：首屏只渲染前 12,000 字，余下**在浏览器空闲时间**分片续渲染（用户无感，
// 不遮内容、不改变 DOM 最终形态）；流式消息长度变化时自动续到最新。
const LONG_TEXT_THRESHOLD = 20_000;
const LONG_TEXT_INITIAL = 12_000;
const LONG_TEXT_STEP = 40_000;

const _idle: (cb: () => void) => number =
  typeof window !== 'undefined' && 'requestIdleCallback' in window
    ? (cb) => (window as unknown as { requestIdleCallback: (c: () => void, o?: { timeout: number }) => number })
        .requestIdleCallback(cb, { timeout: 300 })
    : (cb) => window.setTimeout(cb, 16);
const _cancelIdle: (h: number) => void =
  typeof window !== 'undefined' && 'cancelIdleCallback' in window
    ? (h) => (window as unknown as { cancelIdleCallback: (h: number) => void }).cancelIdleCallback(h)
    : (h) => window.clearTimeout(h);

// AI 消息 Markdown 渲染（GFM 表格/删除线/任务列表）。净化纪律：sanitizeAiText
// 剥幻觉引用链接，必须发生在 Markdown 解析之前。纯渲染组件，流式每帧重渲染直接吃增量全文
const MarkdownText: FC<MarkdownTextProps> = ({ content, className = '', components }) => {
  const total = content?.length ?? 0;
  const [rendered, setRendered] = useState(() =>
    total > LONG_TEXT_THRESHOLD ? LONG_TEXT_INITIAL : total);

  // 内容变化（换篇 / 流式增长）：短文直接全量；长文按窗口续渲染
  useEffect(() => {
    if (total <= LONG_TEXT_THRESHOLD) {
      setRendered(total);
    } else {
      setRendered((r) => Math.min(Math.max(r, LONG_TEXT_INITIAL), total));
    }
  }, [total]);

  // 空闲续渲染（分片推进，避免一次性阻塞主线程）
  useEffect(() => {
    if (rendered >= total) return;
    const h = _idle(() => setRendered((r) => Math.min(r + LONG_TEXT_STEP, total)));
    return () => _cancelIdle(h);
  }, [rendered, total]);

  if (!content) return null;
  const shown = rendered >= total ? content : content.slice(0, rendered);
  return (
    <div className={`text-sm leading-relaxed text-text-primary ${className}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} urlTransform={urlTransform} components={{ ...COMPONENTS, ...components }}>
        {sanitizeAiText(shown)}
      </ReactMarkdown>
      {rendered < total && (
        <div className="mt-2 text-xs text-text-secondary">
          长文分段渲染中…（已显示 {rendered.toLocaleString()} / {total.toLocaleString()} 字）
        </div>
      )}
    </div>
  );
};

export default MarkdownText;
